import {
  Briefcase,
  Calculator,
  FileSignature,
  ArrowLeftRight,
  Shield,
  Users,
  CreditCard,
  FolderOpen,
  BarChart3,
} from 'lucide-react';

const ORANGE = '#f97316';
const AMBER = '#f59e0b';
const AMBER_LIGHT = '#fbbf24';

const features = [
  {
    icon: Briefcase,
    title: 'Job Management',
    desc: 'Create and track every job from first contact to final walkthrough. Status, timeline, budget, and field crew — all in one view.',
    accent: ORANGE,
  },
  {
    icon: Calculator,
    title: 'Estimates & Proposals',
    desc: 'Build line-item estimates with markups, send polished proposals, and convert accepted proposals to contracts in one click.',
    accent: AMBER,
  },
  {
    icon: FileSignature,
    title: 'Contracts & E-Sign',
    desc: 'Generate contracts from templates, send for electronic signature, and track execution status. No more chasing wet signatures across job sites.',
    accent: ORANGE,
  },
  {
    icon: ArrowLeftRight,
    title: 'Change Orders',
    desc: 'Document scope changes with cost impact, get client approval, and automatically update the job budget. Full audit trail for every dollar.',
    accent: AMBER_LIGHT,
  },
  {
    icon: Shield,
    title: 'Lien Waivers',
    desc: 'No more lien-waiver nightmares. Generate conditional and unconditional waivers, track sub signatures, and enforce compliance before cutting checks.',
    accent: ORANGE,
  },
  {
    icon: Users,
    title: 'Subcontractor Management',
    desc: 'Own your GC/sub relationships. Manage your roster, assign jobs, track insurance and licensing, and dispatch work through the Sub Portal.',
    accent: AMBER,
  },
  {
    icon: CreditCard,
    title: 'Payments & Billing',
    desc: 'Track draw schedules, progress billing, and payment history. Know exactly what is owed and what has been paid on every job.',
    accent: AMBER_LIGHT,
  },
  {
    icon: FolderOpen,
    title: 'Document Management',
    desc: 'Centralized file storage per job. Plans, permits, photos, receipts — everything organized and accessible from the field on any device.',
    accent: ORANGE,
  },
  {
    icon: BarChart3,
    title: 'Reports & Insights',
    desc: 'Job profitability, cash flow projections, sub performance, and more. Make decisions based on real numbers, not gut feel.',
    accent: AMBER,
  },
];

export function FeaturesSection() {
  return (
    <section id="features" className="relative py-28 sm:py-32">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(17,17,17,0) 0%, rgba(26,26,26,0.5) 50%, rgba(17,17,17,0) 100%)',
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6">
        <div className="mb-16 text-center reveal">
          <span className="mb-4 inline-block rounded-full border border-build-700/40 bg-build-900/40 px-3 py-1 text-xs font-medium text-orange-400">
            Features
          </span>
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Everything a Contractor Needs
          </h2>
          <p className="mx-auto max-w-2xl text-zinc-400">
            From the first estimate to the final lien waiver. Every tool your construction business needs to run clean, organized, and profitable.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div
              key={f.title}
              className="reveal feature-card group relative rounded-2xl border border-build-700/30 bg-build-900/20 p-6 pl-7 backdrop-blur"
              style={{ borderLeft: `2px solid ${f.accent}` }}
            >
              <div
                className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ background: `${f.accent}15` }}
              >
                <f.icon className="h-5 w-5" style={{ color: f.accent }} />
              </div>
              <h3 className="mb-2 text-base font-semibold">{f.title}</h3>
              <p className="text-sm leading-relaxed text-zinc-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
